import { SyntheticEvent, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Rating,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { format, isToday, isYesterday } from "date-fns";
import { es } from "date-fns/locale";
import Frame from "@/components/Frame";
import { getInitials, stringToColor } from "@/utils/names";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon } from "@hugeicons/core-free-icons";
import { Question, SurveyResponse } from "@/types/surveys";

export interface SurveyResponsesListProps {
  responses: SurveyResponse[];
  questions?: Question[];
  organizationId: string;
  orderNumberByOrderId?: Map<string, string | number>;
  isLoading?: boolean;
}

const getDateValue = (rawDate: unknown): Date | string | number | undefined => {
  if (!rawDate) return undefined;
  if (rawDate instanceof Date) return rawDate;
  if (
    typeof rawDate === "object" &&
    rawDate !== null &&
    "toDate" in rawDate &&
    typeof (rawDate as any).toDate === "function"
  ) {
    return (rawDate as any).toDate();
  }
  if (
    typeof rawDate === "object" &&
    rawDate !== null &&
    "seconds" in rawDate &&
    typeof (rawDate as any).seconds === "number"
  ) {
    return new Date((rawDate as any).seconds * 1000);
  }
  if (typeof rawDate === "string" || typeof rawDate === "number") {
    return rawDate;
  }
  return undefined;
};

const formatDateFriendly = (rawDate: unknown): string => {
  const d = getDateValue(rawDate);
  if (!d) return "";
  const dateObj = d instanceof Date ? d : new Date(d);
  if (isNaN(dateObj.getTime())) return "";

  if (isToday(dateObj)) {
    return `Hoy a las ${format(dateObj, "h:mm a")}`;
  }
  if (isYesterday(dateObj)) {
    return `Ayer a las ${format(dateObj, "h:mm a")}`;
  }
  return format(dateObj, "d MMM, yyyy 'a las' h:mm a", { locale: es });
};

const SurveyResponsesList = ({
  responses,
  questions = [],
  organizationId,
  orderNumberByOrderId,
  isLoading,
}: SurveyResponsesListProps) => {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | false>(false);
  const [visibleResponsesCount, setVisibleResponsesCount] = useState<number>(5);

  const handleAccordionChange =
    (panel: string) => (_event: SyntheticEvent, isExpanded: boolean) => {
      setExpandedId(isExpanded ? panel : false);
    };

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" py={6} spacing={1.5}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Cargando respuestas...
        </Typography>
      </Stack>
    );
  }

  if (!responses.length) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Aún no hay respuestas individuales registradas para esta encuesta.
        </Typography>
      </Box>
    );
  }

  const displayedResponses = responses.slice(0, visibleResponsesCount);
  const hasMoreResponses = visibleResponsesCount < responses.length;

  return (
    <Stack spacing={2}>
      {/* Lista de respuestas individuales con MUI Accordion */}
      <Stack spacing={1.5}>
          {displayedResponses.map((resp, idx) => {
            const responseKey = resp.id || `resp-${idx}`;
            const isExpanded = expandedId === responseKey;
            const rawAnswers = resp.answers as any;
            const displayName = resp.clientName?.trim() || "Anónimo";
            const initials = getInitials(displayName);
            const avatarBg = stringToColor(displayName);
            const resolvedOrderNumber =
              resp.orderNumber !== undefined &&
                resp.orderNumber !== null &&
                resp.orderNumber !== ""
                ? resp.orderNumber
                : resp.orderId
                  ? orderNumberByOrderId?.get(resp.orderId)
                  : undefined;

            return (
              <Accordion
                key={responseKey}
                expanded={isExpanded}
                onChange={handleAccordionChange(responseKey)}
                disableGutters
                elevation={0}
                sx={{
                  border: isExpanded ? "1.5px solid" : "1px solid",
                  borderColor: isExpanded ? "primary.main" : "grey.200",
                  borderRadius: "10px !important",
                  "&:before": { display: "none" },
                  backgroundColor: "background.paper",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        bgcolor: isExpanded
                          ? "rgba(1, 95, 238, 0.12)"
                          : "rgba(1, 95, 238, 0.08)",
                        color: "primary.main",
                      }}
                    >
                      <HugeiconsIcon icon={ArrowDown01Icon} size={18} />
                    </Avatar>
                  }
                  sx={{
                    px: 2,
                    py: 1,
                    minHeight: 64,
                    "& .MuiAccordionSummary-content": {
                      my: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    },
                    "& .MuiAccordionSummary-content.Mui-expanded": {
                      my: 0,
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      ml: 1.5,
                      transition: "transform 0.2s ease",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  {/* Izquierda: Avatar con iniciales y fecha con formato moment */}
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Tooltip title={displayName}>
                      <Avatar
                        sx={{
                          width: 38,
                          height: 38,
                          fontSize: "0.875rem",
                          bgcolor: avatarBg,
                          color: "#FFFFFF",
                        }}
                      >
                        {initials}
                      </Avatar>
                    </Tooltip>
                    <Typography
                      variant="body2"
                    >
                      {formatDateFriendly(resp.createdAt)}
                    </Typography>
                  </Stack>

                  {/* Derecha: Chip de orden */}
                  <Box sx={{ mr: 0.5 }}>
                    {resp.orderId ? (
                      <Chip
                        size="small"
                        color="primary"
                        variant="filled"
                        clickable
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/comercios/${organizationId}/pedidos/${resp.orderId}?tab=orders`
                          );
                        }}
                        label={
                          resolvedOrderNumber !== undefined &&
                            resolvedOrderNumber !== null &&
                            resolvedOrderNumber !== ""
                            ? resolvedOrderNumber
                            : resp.orderId.slice(0, 8)
                        }
                        sx={{
                          fontSize: "0.75rem",
                          px: 0.5,
                          "& .MuiChip-icon": { ml: 0.5 },
                        }}
                      />
                    ) : null}
                  </Box>
                </AccordionSummary>

                {/* Contenido expandible con Frame para cada par pregunta/respuesta */}
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0.5 }}>
                  <Stack spacing={1.5}>
                    {questions.map((q, qIndex) => {
                      const qKey = q.id || String(qIndex);
                      let val: any;
                      if (Array.isArray(rawAnswers)) {
                        val = rawAnswers.find(
                          (a: any) => a.question === q.title
                        )?.answer;
                      } else if (rawAnswers && typeof rawAnswers === "object") {
                        val = rawAnswers[qKey] ?? rawAnswers[q.title];
                      }

                      if (val === undefined || val === null || val === "")
                        return null;

                      return (
                        <Frame
                          key={qKey}
                          deep={1}
                          sx={{
                            backgroundColor: "#F8FAFC",
                            border: "none",
                            borderRadius: 2,
                            p: 2,
                          }}
                        >
                          <Stack spacing={1}>
                            <Typography variant="body1" fontWeight="bold">
                              {q.title}
                            </Typography>
                            {q.type === "rating" ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <Rating
                                  readOnly
                                  value={Number(val)}
                                  size="small"
                                  sx={{ color: "#faaf00" }}
                                />
                                <Typography variant="body1">
                                  {Number(val)} / 5
                                  {Number(val) === 5
                                    ? " (Máxima calificación)"
                                    : ""}
                                </Typography>
                              </Stack>
                            ) : q.type === "text" ? (
                              <Typography variant="body1">
                                "{String(val)}"
                              </Typography>
                            ) : (
                              (() => {
                                const selectedIds = Array.isArray(val)
                                  ? val
                                  : [val];
                                const labels = selectedIds.map((id) => {
                                  const found = (q.options ?? []).find(
                                    (o, oIdx) =>
                                      (o.id || String(oIdx)) === String(id) ||
                                      o.title === String(id)
                                  );
                                  return found ? found.title : String(id);
                                });
                                return (
                                  <Typography variant="body1">
                                    {labels.join(", ")}
                                  </Typography>
                                );
                              })()
                            )}
                          </Stack>
                        </Frame>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>

        {/* Botón para cargar 5 más si hay disponibles */}
        {hasMoreResponses && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setVisibleResponsesCount((prev) => prev + 5)}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 0.8,
                borderColor: "rgba(1, 95, 238, 0.3)",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "rgba(1, 95, 238, 0.04)",
                },
              }}
            >
              Ver más respuestas
            </Button>
          </Box>
        )}
      </Stack>
  );
};

export default SurveyResponsesList;
